import React, { useState, useEffect } from 'react';
import {
    Form,
    Input,
    Select,
    Row,
    Col,
    Button,
    Space,
    Modal,
    DatePicker,
} from 'antd';
import { apiRequest } from '../../../../service/apiRequest';
import { showSuccess, showError } from '../../../../components/errorHandler';
import { WoodType } from "../../../../types/WoodType";



interface WoodModalProps {
    visible: boolean;
    onCancel: () => void;
    onCreate: (data: any) => void; // Callback after successful submit
    initialValues?: {
        wood_id?: number;
        wood_name?: string;
        wood_thickness?: number;
        wood_length?: number;
        wood_width?: number;
        wood_type?: number;
    } | null;
    isEditMode?: boolean;
    woodTypesData?: WoodType[];
}
const WoodModal: React.FC<WoodModalProps> = ({
    visible,
    onCancel,
    onCreate,
    initialValues = null,
    isEditMode = false,
    woodTypesData = [],
}) => {
    const [form] = Form.useForm();
    const [isModalClose, setIsModalClose] = useState(true);

    // Determine if submit button should be disabled based on form validation
    const isSubmitDisabled = () => {
        const errors = form.getFieldsError();
        return errors.some(({ errors }) => errors.length > 0);
    };


    // Handle form submit
    const handleAddwood = async (values: any) => {
        try {
            const dataVal = {
                wood_name: values.wood_name,
                wood_thickness: values.wood_thickness,
                wood_length: values.wood_length,
                wood_width: values.wood_width,
                wood_type: values.wood_type,
                ...(initialValues?.wood_id && { wood_id: initialValues.wood_id }),
            };
            const url = isEditMode
                ? 'edit-wood'
                : 'add-wood';

            const method = isEditMode ? 'put' : 'post';

            const data = await apiRequest({
                method,
                url,
                data: dataVal,
            });
            console.log(dataVal);

            onCreate(data); // Notify parent about success
            showSuccess(
                isEditMode ? 'Wood updated successfully!' : 'Wood created successfully!',
            );
            form.resetFields();

            if (isModalClose) {
                onCancel(); // Close modal only if Submit button was clicked (not Submit & Create New)
            }
        } catch (error) {
            showError(`Failed to ${isEditMode ? 'update' : 'create'} Wood`);
        }
    };

    // When modal opens or mode changes, set/reset form fields accordingly
    useEffect(() => {
        if (visible && isEditMode && initialValues) {
            form.setFieldsValue({
                wood_name: initialValues.wood_name,
                wood_thickness: initialValues.wood_thickness,
                wood_length: initialValues.wood_length,
                wood_width: initialValues.wood_width,
                wood_type: initialValues.wood_type,
            });
        } else if (visible) {
            form.resetFields();
        }
    }, [visible, isEditMode, initialValues, form]);




    return (
        <Modal
            title={isEditMode ? 'Edit Wood' : 'Add New Wood'}
            visible={visible}
            onCancel={onCancel}
            footer={null}
            width="90%"
            destroyOnClose
        >
            <Form
                form={form}
                layout="vertical"
                onFinish={handleAddwood}
                autoComplete="off"
            >
                <Row gutter={16}>
                    {/* Wood Type Dropdown (uses wood_type ID as value) */}
                    <Col span={12}>
                        <Form.Item
                            name="wood_type"
                            label={
                                <span>
                                    Wood Type &nbsp;
                                </span>
                            }
                            rules={[{ required: true, message: 'Please select wood type' }]}
                        >
                            <Select
                                showSearch
                                filterOption={(input, option) =>
                                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                }
                                placeholder="Select wood type"
                                options={woodTypesData.map(wt => ({ label: wt.wood_name, value: wt.wood_id }))}
                            />
                        </Form.Item>
                    </Col>
                    {/* Wood Name */}
                    <Col span={12}>
                        <Form.Item
                            name="wood_name"
                            label="Wood Name"
                            rules={[
                                { required: true, message: 'Please enter the Wood Name' },
                                { max: 50, message: 'Maximum 50 characters allowed' },
                            ]}
                        >
                            <Input maxLength={50} placeholder="e.g., African Mahogany" />
                        </Form.Item>
                    </Col>

                    {/* Wood Thickness */}
                    <Col span={12}>
                        <Form.Item
                            name="wood_thickness"
                            label="Thickness (In Inch)"
                            rules={[{ required: true, message: 'Please enter wood thickness' }]}
                        >
                            <Input placeholder="e.g., 5Inch" />
                        </Form.Item>
                    </Col>

                    {/* Wood Length */}
                    <Col span={12}>
                        <Form.Item
                            name="wood_length"
                            label="Length (In Inch)"
                            rules={[{ required: true, message: 'Please enter wood length' }]}
                        >
                            <Input placeholder="e.g., 10inch" />
                        </Form.Item>
                    </Col>

                    {/* Wood Width */}
                    <Col span={12}>
                        <Form.Item
                            name="wood_width"
                            label="Width (In Inch)"
                            rules={[{ required: true, message: 'Please enter wood width' }]}
                        >
                            <Input placeholder="e.g., 10Inch" />
                        </Form.Item>
                    </Col>


                </Row>


                <Form.Item style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Space>
                        <Button onClick={onCancel}>Cancel</Button>
                        {!isEditMode && <Button
                            type="primary"
                            htmlType="submit"
                            onClick={() => setIsModalClose(false)}
                            disabled={isSubmitDisabled()}
                        >
                            Submit & Create New Wood Type
                        </Button>}
                        <Button
                            type="primary"
                            htmlType="submit"
                            onClick={() => setIsModalClose(true)}
                            disabled={isSubmitDisabled()}
                        >
                            Submit
                        </Button>
                    </Space>
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default WoodModal;
